import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import PublicRoute from './PublicRoute';
import Landing from '../pages/Landing';
import PrivateRoute from './PrivateRoute';
import MainLayout from '../layouts/MainLayout';
import RequestTableWithQueryParams from '../components/RequestTableWithQueryParams';
import CommonRequestTableWithQueryParams from '../components/CommonRequestsTableWithQueryParams';
import RequestsFiltered from '../pages/RequestsFiltered';
import EntryTableWithQueryParams from '../components/EntriesTableWithQueryParams';
import LoadingContent from '../components/LoadingContent';
import { UserRoleEnum } from '../common/interfaces/user-profile.interface';
import RoleGuard from '../common/guards/RoleGuard';
import CompanyTableWithQueryParams from '../components/ComapniesTableWithQueryParams';
import { useUserProfile } from '../contexts/user/UserContext';
import ClientTableWithQueryParams from '../components/ClientsTableWithQueryParams';
import SuperAdminDashboardWithQueryParams from '../components/SuperadminDashboardWithQueryParams';
import AdminDashboardWithQueryParams from '../components/AdminDashboardWithQueryParams';
import ProfileTableWithQueryParams from '../components/ProfilesTableWithQueryParams';
import { CertificateConfigurator } from '../components/CertificateConfigurator';
import { ProfileStatus } from '../common/interfaces/profile.interface';
import { CompanyStatus } from '../common/interfaces/company.interface';
import InactivePage from '../pages/InactivePage';
import { AnnexConfigurator } from '../components/AnnexConfigurator';

const Router = () => {
  const { user, isLoading } = useUserProfile();

  if (isLoading) {
    return <LoadingContent />;
  }

  return (
    <BrowserRouter>
      <QueryParamProvider adapter={ReactRouter6Adapter}>
        <Routes>
          {!user && <Route path="/" element={<PublicRoute element={<Landing />} />} />}
          {/* Admin Rutes */}
          {(user && user.status === ProfileStatus.INACTIVE) ||
            (user && user.company?.status === CompanyStatus.INACTIVE && (
              <>
                <Route path="/inactive" element={<InactivePage />} />
              </>
            ))}
          {user && user.role !== UserRoleEnum.SUPER_ADMIN && (
            <Route path="/" element={<PrivateRoute element={<MainLayout />} />}>
              <Route index element={<Navigate to="clients" />}></Route>

              <Route path="clients" element={<ClientTableWithQueryParams />} />

              <Route
                path="/dashboard"
                element={
                  <RoleGuard roles={[UserRoleEnum.ADMIN]}>
                    <AdminDashboardWithQueryParams />
                  </RoleGuard>
                }
              />
              <Route
                path="/users"
                element={
                  <RoleGuard roles={[UserRoleEnum.ADMIN]}>
                    <ProfileTableWithQueryParams />
                  </RoleGuard>
                }
              />

              <Route path="/clients/:clientId" element={<Outlet />}>
                <Route index element={<Navigate to="requests" />} />
                <Route path="requests" element={<RequestTableWithQueryParams />} />
                <Route
                  path="certificate"
                  element={
                    <RoleGuard roles={[UserRoleEnum.ADMIN]}>
                      <CertificateConfigurator />
                    </RoleGuard>
                  }
                />
                <Route
                  path="annex"
                  element={
                    <RoleGuard roles={[UserRoleEnum.ADMIN]}>
                      <AnnexConfigurator />
                    </RoleGuard>
                  }
                />
                <Route
                  path="requests/:requestId/:year/:month"
                  element={<EntryTableWithQueryParams />}
                />
                <Route path="common" element={<CommonRequestTableWithQueryParams />} />
                <Route path="common/:year/:month" element={<RequestsFiltered />} />
              </Route>
            </Route>
          )}

          {/* // Super admin route */}
          {user && user.role === UserRoleEnum.SUPER_ADMIN && (
            <Route path="/" element={<PrivateRoute element={<MainLayout />} />}>
              <Route index element={<Navigate to="companies" />} />

              <Route
                path="/companies"
                element={
                  <RoleGuard roles={[UserRoleEnum.SUPER_ADMIN]}>
                    <CompanyTableWithQueryParams />
                  </RoleGuard>
                }
              />

              <Route
                path="/dashboard"
                element={
                  <RoleGuard roles={[UserRoleEnum.SUPER_ADMIN]}>
                    <SuperAdminDashboardWithQueryParams />
                  </RoleGuard>
                }
              />

              <Route path="/companies/:companyId" element={<Navigate to="clients" />} />

              <Route
                path="/companies/:companyId/clients"
                element={<ClientTableWithQueryParams />}
              />

              <Route
                path="/companies/:companyId/dashboard"
                element={
                  <RoleGuard roles={[UserRoleEnum.SUPER_ADMIN]}>
                    <AdminDashboardWithQueryParams />
                  </RoleGuard>
                }
              />

              <Route
                path="/companies/:companyId/users"
                element={
                  <RoleGuard roles={[UserRoleEnum.SUPER_ADMIN]}>
                    <ProfileTableWithQueryParams />
                  </RoleGuard>
                }
              />

              <Route path="/companies/:companyId/clients/:clientId" element={<Outlet />}>
                <Route index element={<Navigate to="requests" />} />
                <Route path="requests" element={<RequestTableWithQueryParams />} />
                <Route path="certificate" element={<CertificateConfigurator />} />
                <Route path="annex" element={<AnnexConfigurator />} />
                <Route
                  path="/companies/:companyId/clients/:clientId/requests/:requestId/:year/:month"
                  element={<EntryTableWithQueryParams />}
                />
                <Route
                  path="/companies/:companyId/clients/:clientId/common"
                  element={<CommonRequestTableWithQueryParams />}
                />
                <Route
                  path="/companies/:companyId/clients/:clientId/common/:year/:month"
                  element={<RequestsFiltered />}
                />
              </Route>
            </Route>
          )}

          <Route path="*" element={<Navigate to="/" replace={true} />} />
        </Routes>
      </QueryParamProvider>
    </BrowserRouter>
  );
};

export default Router;
